import { useEffect } from 'react';
import { useRouter } from 'next/router';

// Types
import type { NotificationSchedulerProps } from '@/utils/hooks/context/toast/types';
import type { SocialProfileType } from '@/types/socialProfileType';

// Routes
import { SCHEDULER } from '@/routes/privateRoutes';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useSelectedSocialProfileById from '@/utils/hooks/socialprofiles/useSelectedSocialProfileById';

// Component
import Icon from '@/components/base/Icon';
import Notification from '@/components/base/Notification';
import PostThumbnail from '@/components/composite/Notifications/Collaboration/components/PostThumbnail';

const PostPublishFailed = ({ notification }: NotificationSchedulerProps) => {
    const router = useRouter();
    const { setActiveSocialProfile } = useActiveSocialProfile();
    const { selectedSocialProfile } = useSelectedSocialProfileById(
        notification.data?.social_profile_id as SocialProfileType['id']
    );

    const url = `${SCHEDULER}?preview_post_id=${notification.data?.scheduler_post?.id}`;
    // Prefetch Overview
    useEffect(() => {
        router.prefetch(url);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Notification
            onClick={() => {
                if (selectedSocialProfile) {
                    setActiveSocialProfile(selectedSocialProfile);
                    router.push(url);
                }
            }}
            dataNotification={notification}
            contentHidden={notification?.options?.hideContent ?? false}
            wide={notification?.options?.wide ?? true}>
            <div className="relative">
                <PostThumbnail
                    thumbnail={notification.data?.scheduler_post?.media_thumbnail}
                    postType={notification.data?.scheduler_post?.type ?? 'image'}
                />
                <div className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-icoRed text-8 text-white">
                    <Icon icon="exclamation-point" />
                </div>
                <div className="absolute -bottom-1.5 -right-1.5">
                    <Notification.Avatar size="xs" socialProfile={selectedSocialProfile} />
                </div>
            </div>
            <Notification.Content>
                <Notification.Title
                    i18nKey="commons:notifications.scheduler_post_publish_failed.title"
                    values={{
                        slug: selectedSocialProfile?.slug,
                        social_network: selectedSocialProfile?.social_network_type
                    }}
                />
                <Notification.Description
                    i18nKey="commons:notifications.scheduler_post_publish_failed.description"
                    values={{
                        social_network: selectedSocialProfile?.social_network_type
                    }}
                />
            </Notification.Content>
        </Notification>
    );
};

export default PostPublishFailed;
